<script setup lang="ts">
import { defineAsyncComponent, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useFathom } from '../composables/fathom'
import FilestackService from '../services/FilestackService'
import { makePictureTree } from '@/services/FilestackServiceMakePictureTree'

const {
  i
} = defineProps<{
  i: any
}>()

const router = useRouter()

const imageLoaded = ref(false)
const imageTree = makePictureTree({
  alt: i.model,
  width: '128px',
  handle: i?.cdn_handle_primary_image
})

/** Quick View */
const BaseModelQuickView = defineAsyncComponent(() => import('./BaseModelsListSingleQuickView.vue'))
const showQuickView = ref(false)
const quickViewModel = ref()
function triggerShowQuickView(model: any) {
  quickViewModel.value = model
  showQuickView.value = true
}
function closeQuickView() {
  showQuickView.value = false
  quickViewModel.value = null
}

const { fathom, FathomEvents } = useFathom()
function trackDatasheetEvent() {
  fathom.trackEvent(FathomEvents.DownloadDatasheet([i.vendor, i.model[0]]))
}
</script>

<template>
  <div
    class="m-3 flex flex-column md:flex-row align-items-center border-round border-1 border-200 p-1 hover:surface-50"
  >
    <div class="h-6rem w-6rem mr-2 flex align-items-center justify-content-center">
      <transition-group name="cdn-image-fade">
        <div
          v-show="!imageLoaded"
          key="preloadImage"
        />
        <picture
          v-show="imageLoaded"
          key="cdnImage"
        >
          <img
            :alt="i.model"
            :src="imageTree.img.src"
            :srcset="imageTree.img.srcset"
            style="max-height: 64px; width: 100%; object-fit: contain"
            @load="imageLoaded = true"
          >
        </picture>
      </transition-group>
    </div>
    <div
      class="text-left w-full md:w-auto p-1 md:p-0 cursor-pointer"
      @click="router.push(`/models/${i.model_uri}`)"
    >
      <div class="d-headline-lg text-red-500">
        {{ i.vendor }}
        {{ i.model[0] }}
      </div>
      <div class="text-sm">
        {{ i.category }}
        <fa-icon
          icon="fa fa-caret-right"
          class="text-primary-300"
        />
        {{ i.category_2 }}
      </div>
    </div>
    <div class="hidden md:inline flex-grow-1" />
    <div class="flex align-items-center">
      <div v-show="i?.cdn_handle_datasheet?.length">
        <a
          v-tooltip.bottom="`View Datasheet`"
          class="hidden md:inline p-button p-component p-button-text p-button-rounded mx-1 text-sm text-red-700 border-1 border-transparent hover:border-red-500 hover:bg-white"
          :disable="!i.cdn_handle_datasheet?.length"
          :href="FilestackService.publicReadUrl(i.cdn_handle_datasheet)"
          target="_blank"
          @click="trackDatasheetEvent"
        >
          <fa-icon
            icon="fa-solid fa-fw fa-file-pdf"
            class="text-xl"
          />
        </a>
        <a
          v-tooltip.bottom="`View Datasheet`"
          class="md:hidden p-button p-button-text p-button-rounded ml-1 text-red-700 hover:bg-red-50 text-decoration-none"
          :disable="!i.cdn_handle_datasheet?.length"
          :href="FilestackService.publicReadUrl(i.cdn_handle_datasheet)"
          target="_blank"
        >
          <fa-icon
            icon="fa-solid fa-fw fa-file-pdf"
            class="text-base mr-1"
          />
          <span class="font-bold text-sm">
            Datasheet
          </span>
        </a>
      </div>
      <p-btn
        class="p-button-text p-button-rounded ml-1 text-sm"
        @click="triggerShowQuickView(i)"
      >
        <fa-icon
          icon="fa-solid fa-search"
          class="mr-1"
        />
        <span class="p-button-label">Quick View</span>
      </p-btn>
      <!-- class="hidden md:inline p-button p-button-icon-only p-button-text p-button-rounded ml-1 text-red-700 hover:bg-red-50" -->
    </div>
    <base-model-quick-view
      v-if="showQuickView"
      :i="quickViewModel"
      @close="closeQuickView"
    />
  </div>
</template>
